// A 2D top-down minimap of the generated level, colored by elevation — the same view
// as the hand-drawn plan of the source map, so the winding shape is legible at a glance.

import { levelColor, RAMP_COLOR, START_COLOR, EXIT_COLOR } from "../config";
import type { GeneratedLevel } from "../map/grammar";

const SIZE = 280;
const PAD = 14;

// terra pseudo-tile kinds → biome hazard tints (tile kinds fall through to elevation colors)
const KIND_COLORS: Record<string, string> = {
  lava: "#e8481c",
  water: "#2e6fae",
  slick: "#b8e2f2",
  crevasse: "#0c1420",
};

export class Minimap {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private cache: HTMLCanvasElement; // the static tile+dots layer, rendered once per level
  private cctx: CanvasRenderingContext2D;
  private level: GeneratedLevel | null = null;
  // world→canvas transform, fixed per level (used by both the cache render and the live crosshair)
  private tx = (x: number): number => x;
  private tz = (z: number): number => z;

  constructor(container: HTMLElement) {
    this.canvas = document.createElement("canvas");
    this.canvas.width = SIZE;
    this.canvas.height = SIZE;
    Object.assign(this.canvas.style, {
      position: "fixed",
      right: "12px",
      bottom: "12px",
      width: `${SIZE}px`,
      height: `${SIZE}px`,
      background: "rgba(10,14,18,0.82)",
      border: "1px solid #2a3038",
      borderRadius: "6px",
    } as CSSStyleDeclaration);
    container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext("2d")!;
    this.cache = document.createElement("canvas");
    this.cache.width = SIZE;
    this.cache.height = SIZE;
    this.cctx = this.cache.getContext("2d")!;
  }

  setLevel(lvl: GeneratedLevel): void {
    this.level = lvl;
    // recompute the fixed transform, then bake the static layer (tiles + start/exit dots) once
    const b = lvl.stats.bbox;
    const spanX = Math.max(b.maxX - b.minX, 1);
    const spanZ = Math.max(b.maxZ - b.minZ, 1);
    const s = (SIZE - PAD * 2) / Math.max(spanX, spanZ);
    this.tx = (x: number): number => PAD + (x - b.minX) * s;
    this.tz = (z: number): number => SIZE - PAD - (z - b.minZ) * s;

    const ctx = this.cctx;
    ctx.clearRect(0, 0, SIZE, SIZE);
    const tiles = [...lvl.tiles].sort((a, c) => a.level - c.level); // higher elevations on top
    for (const t of tiles) {
      ctx.fillStyle = KIND_COLORS[t.kind] ??
        (t.kind === "ramp" ? `#${RAMP_COLOR.getHexString()}` : `#${levelColor(t.level).getHexString()}`);
      ctx.fillRect(this.tx(t.x0), this.tz(t.z1), (t.x1 - t.x0) * s, (t.z1 - t.z0) * s);
    }
    const dot = (x: number, z: number, color: string): void => {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(this.tx(x), this.tz(z), 5, 0, Math.PI * 2);
      ctx.fill();
    };
    dot(lvl.start.x, lvl.start.z, `#${START_COLOR.getHexString()}`);
    dot(lvl.exit.x, lvl.exit.z, `#${EXIT_COLOR.getHexString()}`);

    this.draw({ x: lvl.start.x, z: lvl.start.z });
  }

  /** Play mode: move the map to the top-right + shrink so it clears the gun viewmodel + HUD. */
  setCompact(on: boolean): void {
    Object.assign(this.canvas.style, on
      ? { top: "46px", bottom: "auto", width: "170px", height: "170px", opacity: "0.9" }
      : { top: "auto", bottom: "12px", width: `${SIZE}px`, height: `${SIZE}px`, opacity: "1" } as CSSStyleDeclaration);
  }

  /** Per-frame: blit the cached static layer, then draw only the moving camera crosshair. */
  draw(focus: { x: number; z: number }): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, SIZE, SIZE);
    if (!this.level) return;
    ctx.drawImage(this.cache, 0, 0);

    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1;
    const fx = this.tx(focus.x);
    const fz = this.tz(focus.z);
    ctx.beginPath();
    ctx.moveTo(fx - 7, fz);
    ctx.lineTo(fx + 7, fz);
    ctx.moveTo(fx, fz - 7);
    ctx.lineTo(fx, fz + 7);
    ctx.stroke();
  }
}
